﻿/**
 * A suite of tests to determine whether the browser supports features
 * from recent ES specifications, in order to help diagnose tricky browser
 * incompatibilities.  Any failures will be visible as errors in the console.
 * All test results are logged with console.log, so they should not be run
 * unless in JsDebug mode (see the runTestsOnJsDebug method).
 */

function runTestsOnJsDebug() {
    if (!isJsDebugEnabled()) {
        return;
    }

    runTests();
}

function isJsDebugEnabled() {
    return window && window.location && window.location.search &&
        window.location.search.toLowerCase().indexOf("jsdebug=true") > -1;
}

function runTests() {
    console.log(' *****  Performing EcmaScript Feature Tests  *****');
    var start = performance.now();
    _testEs2015Features();
    _testEs2016Features();
    _testEs2017Features();
    _testEs2018Features();
    _testEs2019Features();
    _testEs2020Features();
    _testEs2021Features();
    _testAdditionalFeatures();
    var durationInMs = performance.now() - start;
    console.log(' ***** EcmaScript Feature Tests completed in: ' + durationInMs + 'ms');
}

function _testEs2015Features() {
    _testLetAndConst();
    _testClasses();
    _testArrowFunction();
    _testStringInterpolation();
    _testForOfLoop();
    _testPromises();
    _testMap();
    _testGenerators();
    _testIterable();
}

function _testEs2016Features() {
    _testExponentOperator();
    _testAsyncAwait();
    _testDestructuringAssignment();
}

function _testEs2017Features() {
    _testNewObjectMethods();
    _testStringPadding();

    var knownFailures = [
        _testAtomics.name
    ];

    console.log('There are known failures for ES 2017', knownFailures);
}

function _testEs2018Features() {
    _testSpreadOperator();
    _testRestParameters();
    _testPromiseFinally();
    _testForAwaitOf();
}

function _testEs2019Features() {
    _testArrayFlat();
    _testArrayFlatMap();
    _testObjectFromEntries();
}

function _testEs2020Features() {
    _testNullishCoelescingOperator();
    _testOptionalChainingOperator();
    _testGlobalThis();
    _testBigInt();

    var knownFailures = [
        _testBigInt.name // Literal format (e.g. 123n) cannot be polyfilled
    ];

    console.log('There are known failures for ES 2020', knownFailures);
}

function _testEs2021Features() {
    _testStringReplaceAll();
    _testLogicalAssignmentOperators();
    _testNumericLiteralSeparators();
    _testPromiseAny();
    _testAggregateError();
}

function _testAdditionalFeatures() {
    _testUrlSearchParams();
}

function _testLetAndConst() {
    let mutable = '';
    const immutable = [];
    mutable = 'changed';
    immutable.push('val');
    console.log('Let and Const work', mutable, immutable);
}

function _testClasses() {
    class TestClass {
        testMethod() {
            console.log('Classes work');
        }
    }

    (new TestClass()).testMethod();
}

function _testArrowFunction() {
    (() => console.log('Arrow functions work.'))();
}

function _testStringInterpolation() {
    var stuff = 'stuff';
    var interpolatedString = `This is some ${stuff}`;
    console.log('Interpolated Strings work', interpolatedString);
}

function _testForOfLoop() {
    var sum = 0;
    for (let i of [1, 2, 3, 4, 5]) {
        sum += i;
    }
    console.log('for...of loop works', sum);
}

function _testPromises() {
    Promise.resolve('works')
        .then(function(result) { console.log('Promises work', result) });
}

function _testMap() {
    var map = new Map();
    map.set('a', 1);
    console.log('Map works', map.get('a'));
}

function _testIterable() {
    if (!Symbol || !Symbol.iterator) throw new Error('Symbol.iterator is not defined');

    var range = {
        from: 1,
        to: 5
    };

    range[Symbol.iterator] = function () {
        return {
            current: this.from,
            last: this.to,
            next() {
                if (this.current <= this.last) {
                    return { done: false, value: this.current++ };
                } else {
                    return { done: true };
                }
            }
        };
    };

    var testIfRangeIsIterable = Array.from(range);
    if (!testIfRangeIsIterable.length) throw new Error('Test Object is not iterable');
    var map = new Map();
    map.set('a', 1);
    var testIfMapIsIterable = Array.from(map);
    if (!testIfMapIsIterable.length) throw new Error('Map is not iterable');
    console.log('Test Object and Map are iterable', testIfMapIsIterable);
}

function _testGenerators() {
    function* range(start, end, step) {
        while (start < end) {
            yield start;
            start += step;
        }
    }

    var sum = 0;
    for (let i of range(0, 10, 2)) {
        sum += i;
    }

    console.log('Generators work', sum);
}

function _testExponentOperator() {
    var cube = 3 ** 3;
    console.log('Exponent operator works', cube);
}

function _testAsyncAwait() {
    async function testAsync() {
        return await Promise.resolve('foo');
    }

    testAsync()
        .then(function(result) { console.log('async/await works', result); });
}

function _testDestructuringAssignment() {
    var [a, b, ...rest] = [1, 2, 3, 4, 5];
    console.log('Destructuring Assignment works', a, b, rest);
}

function _testNewObjectMethods() {
    var obj = { a: 1, b: 2, c: 3 };
    var values = Object.values(obj);
    var entries = Object.entries(obj);
    var descriptors = Object.getOwnPropertyDescriptors(obj);

    console.log('New Object methods work', values, entries, descriptors);
}

function _testStringPadding() {
    var startPadded = '123'.padStart(5, '0');
    var endPadded = '123'.padEnd(5, '0');

    console.log('String padding works', startPadded, endPadded);
}

function _testAtomics() {
    var int32Array = Int32Array.from([123,456,789,234,567]);
    var result = Atomics.xor(int32Array, 2, 123);

    console.log('Atomics work', result);
}

function _testSpreadOperator() {
    var arr = [...[1, 2, 3, 4], 5, 6];

    console.log('Spread operator works', arr);
}

function _testRestParameters() {
    function test(param1, param2, ...theRest) {
        console.log('Rest parameters work', param1, param2, theRest);
    }

    test(1, 2, 3, 4, 5, 6);
}

function _testPromiseFinally() {
    Promise.reject('test')
        .catch(function() {})
        .finally(function() { console.log('Promise.finally works') });
}

function _testForAwaitOf() {
    const asyncIterable = {
        [Symbol.asyncIterator]() {
            return {
                i: 0,
                next() {
                    if (this.i < 3) {
                        return Promise.resolve({ value: this.i++, done: false });
                    }

                    return Promise.resolve({ done: true });
                }
            };
        }
    };

    (async function () {
        var x = [];
        for await (let num of asyncIterable) {
            x.push(num);
        }
        return x;
    })()
        .then(function(result) { console.log('for await...of works', result)});
}

function _testArrayFlat() {
    var arr = [0, 1, 2, [3, 4]];
    var flatArr = arr.flat();

    console.log('Array.prototype.flat works', flatArr);
}

function _testArrayFlatMap() {
    var arr = [0, 1, 2, 3, 4];
    var flatMapArr = arr.flatMap(function(x) { return [x * 2]; });

    console.log('Array.prototype.flatMap works', flatMapArr);
}

function _testObjectFromEntries() {
    var entries =[
        ['foo', 'bar'],
        ['baz', 42]
    ];

    var obj = Object.fromEntries(entries);

    console.log('Object.fromEntries works', obj);
}

function _testBigInt() {
/*  NOTE:  Causes a compile error in IE if uncommented */
/*
    var bigInt = 10n;

    console.log('BigInt works', bigInt);
    */
}

function _testNullishCoelescingOperator() {
    var a = 'foo' ?? 'bar';
    var b = null ?? 'baz';

    console.log('Nullish Coelescing Operator works', a, b);
}


function _testOptionalChainingOperator() {
    var obj = {
        prop1: 'foo',
        arr1: [1,2,3]
    };

    var a = obj?.prop1;
    var b = obj.arr1?.pop();
    var x = obj?.noSuchProp;
    var y = obj.arr1.noSuchMethod?.();

    console.log('Optional Chaining Operator works', a, b, x, y);
}


function _testGlobalThis() {
    var globalThisProto = globalThis.prototype;

    console.log('globalThis works', globalThisProto);
}

function _testStringReplaceAll() {
    var str = 'aabcdaaefgaa'.replaceAll('aa', 'zz');

    console.log('String.prototype.replaceAll works', str);
}

function _testPromiseAny() {
    Promise.any([Promise.resolve('test1'), Promise.resolve('test2')])
        .then(function(result) { console.log('Promise.any works', result); });
}


function _testAggregateError() {
    var err = new AggregateError([new Error('err1'), new Error('err2')], 'test aggregate error');

    console.log('AggregateError works', err);
}


function _testLogicalAssignmentOperators() {
    var x = null;
    x ??= 'foo';
    var y = null;
    y ||= 'bar';
    var z = 'baz';
    z &&= 'jenga';

    console.log('Logical Assignment Operators work', x, y, z);
}


function _testNumericLiteralSeparators() {
    var num = 1_234;

    console.log('Numeric Literal Separators work', num);
}

function _testUrlSearchParams() {
    var params = new URLSearchParams('param1=val1&param2=val2');
    var param1 = params.get('param1');
    var param2 = params.get('param2');

    console.log('URLSearchParams works', param1, param2);
}


export default { runTests, runTestsOnJsDebug }
